/**
 * Phase 114.A — Gradebook page (foundation MVP).
 *
 * Pro-gated assessment layer. Strict privacy: students are anonymous
 * teacher-assigned numbers (Student 1, 2, … or custom labels like 101,
 * 102). No names ever enter the system.
 *
 * 114.A covers:
 *  - 3-step setup wizard (student count → unit confirmation → ready)
 *  - Unit view with sidebar of subjects/units + assessment grid
 *  - "+ Add assessment" inline form
 *  - Inline mark entry with the Ontario granular level scale
 *    (R, 1-, 1, 1+, 2-, 2, 2+, 3-, 3, 3+, 4-, 4, 4+) for summative
 *    assessments; ✓/✗ for formative/observation
 *  - Auto-save on every entry
 *
 * 114.B adds AI marking; 114.C adds per-student feedback + the
 * Snapshot/Student views; 114.D wires Report Card Builder context.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams, Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
  BookOpen,
  CalendarClock,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ClipboardCheck,
  ExternalLink,
  FileText,
  Loader2,
  Plus,
  Printer,
  Sparkles,
  Trash2,
  Users,
  X,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { useAuth } from "../contexts/AuthContext";
import { isPaidUser } from "../lib/tier";
import { isLrpSubjectKeyVisible } from "../lib/subjects";
import {
  createGradebookAssessment,
  deleteGradebookAssessment,
  getGradebookSetupStatus,
  getGradebookStudents,
  getGradebookUnits,
  gradebookRubricPdfClassUrl,
  listGradebookAssessments,
  listGradebookResults,
  saveGradebookStudents,
  upsertGradebookResult,
} from "../lib/api";
import RubricPanel from "../components/RubricPanel";

const FIELD_CLASS =
  "bg-[#f4f5f7] border border-[#4a6984]/20 text-[#4a6984] font-medium focus:ring-2 focus:ring-[#96ded1] focus-visible:ring-[#96ded1] rounded-md h-9 text-sm";

const ONTARIO_LEVELS = [
  "R",
  "1-",
  "1",
  "1+",
  "2-",
  "2",
  "2+",
  "3-",
  "3",
  "3+",
  "4-",
  "4",
  "4+",
];

const PERIOD_LABEL = {
  progress_report: "Progress Report",
  term_1: "Term 1",
  term_2: "Term 2",
};

// Subject label map mirrors the Long Range Plan canonical keys.
const SUBJECT_LABEL = {
  language: "Language",
  math: "Mathematics",
  science: "Science and Technology",
  social_studies: "Social Studies",
  arts: "The Arts",
  health: "Health & PE",
  fsl: "Core French",
};

// ============================================================================
// Setup wizard
// ============================================================================

const SetupWizard = ({ status, onComplete }) => {
  const [step, setStep] = useState(1);
  const [count, setCount] = useState(status?.unit_count ? 24 : 20);
  const [saving, setSaving] = useState(false);

  const handleSaveStudents = async () => {
    setSaving(true);
    try {
      await saveGradebookStudents({ student_count: count });
      setStep(2);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save roster.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto py-12">
      <div className="rounded-lg border border-[#96ded1] bg-white p-7 shadow-sm">
        <div className="flex items-center gap-2 mb-2">
          <span className="ae-pill !text-[10px] bg-[#96ded1]/20 text-[#1c9e7e] border border-[#96ded1]">
            Step {step} of 3
          </span>
          <span className="text-[10px] font-extrabold uppercase tracking-[0.12em] text-[#4a6984]/70">
            Set up your Gradebook
          </span>
        </div>

        {step === 1 ? (
          <>
            <h2 className="text-2xl font-extrabold text-[#1c2d3d]">
              How many students are in your class?
            </h2>
            <p className="mt-2 text-sm text-[#4a6984] leading-snug">
              The platform creates anonymous slots — Student 1 through Student N.
              No names are ever stored. You can relabel slots later (e.g. 101,
              102, or A1, A2) if you use your own numbering.
            </p>
            <div className="mt-5">
              <Label className="text-[#4a6984] font-bold text-xs uppercase tracking-[0.15em]">
                Number of students
              </Label>
              <Input
                type="number"
                min={1}
                max={60}
                value={count}
                onChange={(e) => setCount(Number(e.target.value || 0))}
                data-testid="gradebook-setup-count"
                className={`${FIELD_CLASS} mt-1 max-w-[140px]`}
              />
            </div>
            <div className="mt-6 flex justify-end">
              <Button
                type="button"
                onClick={handleSaveStudents}
                disabled={saving || count < 1}
                data-testid="gradebook-setup-next-1"
                className="bg-[#1c2d3d] hover:bg-[#1c2d3d]/90 text-white font-semibold"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                    Saving…
                  </>
                ) : (
                  <>
                    Next <ChevronRight className="h-4 w-4 ml-1" />
                  </>
                )}
              </Button>
            </div>
          </>
        ) : step === 2 ? (
          <>
            <h2 className="text-2xl font-extrabold text-[#1c2d3d]">
              Units{" "}
              {status?.has_lrp ? "from your Long Range Plan" : "to track"}
            </h2>
            <p className="mt-2 text-sm text-[#4a6984] leading-snug">
              {status?.has_lrp
                ? `We pulled in ${status.unit_count} unit${
                    status.unit_count === 1 ? "" : "s"
                  } from your Long Range Plan. You can add assessments to any of them after setup. Add new units later from the unit picker.`
                : "You don't have a Long Range Plan yet. You'll be able to add units directly from the Gradebook once setup completes."}
            </p>
            <div className="mt-6 flex justify-between">
              <Button
                type="button"
                variant="outline"
                onClick={() => setStep(1)}
                data-testid="gradebook-setup-back-2"
              >
                Back
              </Button>
              <Button
                type="button"
                onClick={() => setStep(3)}
                data-testid="gradebook-setup-next-2"
                className="bg-[#1c2d3d] hover:bg-[#1c2d3d]/90 text-white font-semibold"
              >
                Next <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </>
        ) : (
          <>
            <h2 className="text-2xl font-extrabold text-[#1c2d3d]">
              You&apos;re ready.
            </h2>
            <p className="mt-2 text-sm text-[#4a6984] leading-snug">
              Add assessments within each unit. Marks you confirm here feed
              directly into your Report Card Builder.
            </p>
            <div className="mt-6 flex justify-end">
              <Button
                type="button"
                onClick={onComplete}
                data-testid="gradebook-setup-finish"
                className="bg-[#1c2d3d] hover:bg-[#1c2d3d]/90 text-white font-semibold"
              >
                Open Gradebook
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// Add assessment form
// ============================================================================

const AddAssessmentForm = ({ unit, onCreated, onCancel, initial = null }) => {
  const [name, setName] = useState(initial?.name || "");
  const [type, setType] = useState(initial?.type || "summative");
  const [markType, setMarkType] = useState(initial?.markType || "level");
  const [date, setDate] = useState(initial?.date || new Date().toISOString().slice(0, 10));
  const [successCriteria, setSuccessCriteria] = useState(initial?.successCriteria || "");
  const [codes, setCodes] = useState(initial?.codes || "");
  const [saving, setSaving] = useState(false);
  // Phase 114.B — when the teacher arrived via "Use all criteria" we
  // surface a confirmation summary listing every bundled criterion.
  // The blast radius is larger than the single-criterion path so the
  // summary matters here (per spec).
  const bundleMode = !!initial?.bundleMode;
  const bundleCriteria = Array.isArray(initial?.bundleCriteria)
    ? initial.bundleCriteria
    : [];

  // Summative locks mark_type to "level"
  useEffect(() => {
    if (type === "summative") setMarkType("level");
    else if (markType === "level") setMarkType("completion");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    try {
      const created = await createGradebookAssessment({
        unit_id: unit.id,
        subject: SUBJECT_LABEL[unit.subject_key] || unit.subject_key,
        reporting_period: unit.reporting_period,
        assessment_name: name.trim(),
        assessment_type: type,
        mark_type: markType,
        date,
        success_criteria: successCriteria
          .split(/\n+/)
          .map((s) => s.trim())
          .filter(Boolean),
        curriculum_expectation_codes: codes
          .split(/[,\s]+/)
          .map((s) => s.trim().toUpperCase())
          .filter(Boolean),
      });
      onCreated(created);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add assessment.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-lg border border-[#96ded1] bg-[#96ded1]/10 p-4 space-y-3"
      data-testid="gradebook-add-assessment-form"
    >
      <div className="flex items-center justify-between gap-2">
        <p className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-[#1c2d3d]">
          {bundleMode
            ? `New bundled assessment · ${bundleCriteria.length} criteria`
            : "New assessment"}
        </p>
        <button
          type="button"
          onClick={onCancel}
          className="text-[#4a6984]/60 hover:text-red-600"
          aria-label="Cancel"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      {bundleMode && bundleCriteria.length > 0 ? (
        <div
          className="rounded-md border border-[#1c2d3d]/15 bg-white px-3 py-2.5"
          data-testid="gradebook-add-assessment-bundle-summary"
        >
          <p className="text-[10px] font-extrabold uppercase tracking-[0.14em] text-[#4a6984]">
            Bundling these {bundleCriteria.length} criteria into one column
          </p>
          <ul className="mt-1.5 space-y-1 list-disc list-inside text-[12px] text-[#1c2d3d] leading-snug">
            {bundleCriteria.map((c, i) => (
              <li key={i} className="marker:text-[#96ded1]">
                {c}
              </li>
            ))}
          </ul>
          <p className="text-[10.5px] text-[#4a6984]/85 mt-2 italic leading-snug">
            Click into a cell to mark each criterion on the rubric panel. The
            overall column mark stays your holistic judgment — the platform
            never averages the criterion levels into it.
          </p>
        </div>
      ) : null}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="md:col-span-2">
          <Label className="text-[#4a6984] font-bold text-[10px] uppercase tracking-[0.14em]">
            Assessment name
          </Label>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Fractions Quiz 1"
            data-testid="gradebook-assessment-name"
            className={`${FIELD_CLASS} mt-1`}
            required
          />
        </div>
        <div>
          <Label className="text-[#4a6984] font-bold text-[10px] uppercase tracking-[0.14em]">
            Type
          </Label>
          <Select value={type} onValueChange={setType}>
            <SelectTrigger
              data-testid="gradebook-assessment-type"
              className={`${FIELD_CLASS} mt-1`}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="summative">Summative</SelectItem>
              <SelectItem value="formative">Formative</SelectItem>
              <SelectItem value="observation">Observation</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[#4a6984] font-bold text-[10px] uppercase tracking-[0.14em]">
            Mark type
          </Label>
          <Select
            value={markType}
            onValueChange={setMarkType}
            disabled={type === "summative"}
          >
            <SelectTrigger
              data-testid="gradebook-assessment-mark-type"
              className={`${FIELD_CLASS} mt-1`}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="level">Ontario level scale</SelectItem>
              <SelectItem value="completion" disabled={type === "summative"}>
                Completion ✓✗
              </SelectItem>
            </SelectContent>
          </Select>
          {type === "summative" ? (
            <p className="text-[10px] text-[#4a6984]/70 mt-1 leading-snug">
              Summative tasks always use the Ontario level scale.
            </p>
          ) : null}
        </div>
        <div className="md:col-span-2">
          <Label className="text-[#4a6984] font-bold text-[10px] uppercase tracking-[0.14em]">
            Date
          </Label>
          <Input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            data-testid="gradebook-assessment-date"
            className={`${FIELD_CLASS} mt-1 max-w-[180px]`}
          />
        </div>
        <div className="md:col-span-2">
          <Label className="text-[#4a6984] font-bold text-[10px] uppercase tracking-[0.14em]">
            Success criteria (one per line)
          </Label>
          <Textarea
            value={successCriteria}
            onChange={(e) => setSuccessCriteria(e.target.value)}
            placeholder="Compares fractions with unlike denominators&#10;Represents equivalent fractions visually"
            data-testid="gradebook-assessment-criteria"
            className={`${FIELD_CLASS} mt-1 !h-auto min-h-[80px]`}
            rows={3}
          />
        </div>
        <div className="md:col-span-2">
          <Label className="text-[#4a6984] font-bold text-[10px] uppercase tracking-[0.14em]">
            Curriculum expectation codes (comma- or space-separated)
          </Label>
          <Input
            value={codes}
            onChange={(e) => setCodes(e.target.value)}
            placeholder="B1.3, B1.4"
            data-testid="gradebook-assessment-codes"
            className={`${FIELD_CLASS} mt-1`}
          />
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={saving || !name.trim()}
          data-testid="gradebook-assessment-submit"
          className="bg-[#1c2d3d] hover:bg-[#1c2d3d]/90 text-white font-semibold"
        >
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 mr-1 animate-spin" />
              Adding…
            </>
          ) : (
            <>
              <Plus className="h-4 w-4 mr-1" />
              Add assessment
            </>
          )}
        </Button>
      </div>
    </form>
  );
};

// ============================================================================
// Mark cell — auto-saving Ontario-level / completion picker
// ============================================================================

const MarkCell = ({ value, markType, onChange, testid }) => {
  if (markType === "completion") {
    const isComplete = value === "complete";
    const isIncomplete = value === "incomplete";
    return (
      <div className="flex items-center gap-0.5 justify-center">
        <button
          type="button"
          onClick={() => onChange(isComplete ? null : "complete")}
          data-testid={`${testid}-complete`}
          aria-pressed={isComplete}
          className={`h-7 w-7 rounded text-xs font-extrabold ${
            isComplete
              ? "bg-[#1c9e7e] text-white"
              : "bg-white text-[#4a6984] border border-[#4a6984]/20 hover:border-[#96ded1]"
          }`}
        >
          ✓
        </button>
        <button
          type="button"
          onClick={() => onChange(isIncomplete ? null : "incomplete")}
          data-testid={`${testid}-incomplete`}
          aria-pressed={isIncomplete}
          className={`h-7 w-7 rounded text-xs font-extrabold ${
            isIncomplete
              ? "bg-[#b45309] text-white"
              : "bg-white text-[#4a6984] border border-[#4a6984]/20 hover:border-[#f59e0b]"
          }`}
        >
          ✗
        </button>
      </div>
    );
  }
  return (
    <Select value={value || ""} onValueChange={(v) => onChange(v === "__clear__" ? null : v)}>
      <SelectTrigger
        data-testid={testid}
        className="bg-white border border-[#4a6984]/20 text-[#1c2d3d] font-extrabold h-8 w-[78px] text-sm px-2"
      >
        <SelectValue placeholder="—" />
      </SelectTrigger>
      <SelectContent className="bg-white">
        <SelectItem value="__clear__" className="text-[#4a6984]/60 italic">
          Clear
        </SelectItem>
        {ONTARIO_LEVELS.map((lv) => (
          <SelectItem key={lv} value={lv} className="font-extrabold">
            {lv}
          </SelectItem>
        ))}
        <SelectItem value="needs_review" className="text-[#b45309] italic">
          Needs review
        </SelectItem>
      </SelectContent>
    </Select>
  );
};

// ============================================================================
// Rubric cell button — Phase 114.B
// ============================================================================
//
// For multi-criteria + level assessments, the grid cell becomes a click
// target that opens the per-student rubric panel (instead of an inline
// dropdown). Surface the holistic mark prominently and indicate
// criterion-level progress with a small ratio chip so a teacher can
// scan the grid at a glance.

const RubricCellButton = ({ row, criteriaCount, onClick, testid }) => {
  const markedCount = row?.criterion_levels
    ? Object.values(row.criterion_levels).filter((v) => v).length
    : 0;
  const overall = row?.mark || null;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`group inline-flex items-center justify-between gap-1.5 h-8 px-2 rounded text-xs font-extrabold w-[110px] border transition-colors ${
        overall
          ? "bg-[#1c2d3d] text-white border-[#1c2d3d] hover:bg-[#1c2d3d]/90"
          : "bg-white text-[#4a6984] border-[#4a6984]/20 hover:border-[#1c2d3d] hover:text-[#1c2d3d]"
      }`}
      title="Open rubric panel"
    >
      <span className="truncate">{overall || "—"}</span>
      <span
        className={`text-[9px] font-bold tracking-wide rounded px-1 py-0.5 ${
          overall
            ? "bg-white/20 text-white"
            : "bg-[#f4f5f7] text-[#4a6984]/80 group-hover:bg-[#96ded1]/30"
        }`}
      >
        {markedCount}/{criteriaCount}
      </span>
    </button>
  );
};

// ============================================================================
// Progress overview (Phase 114.D.2.A)
// ============================================================================
//
// Companion tab to the Marks grid. Shows students × every level-mark
// assessment as a state matrix. Three states per cell:
//
//   ○ Not handed in  — default; teacher click flips to ◐
//   ◐ Handed in, not marked  — teacher-set; click flips back to ○
//   ● Marked         — read-only; derived when `mark` or any
//                       `criterion_levels` entry is set. Cannot toggle
//                       submission once a mark exists (clear the mark
//                       in the Rubric Panel / dropdown first).
//
// Completion-mark assessments (formative ✓/✗) are intentionally
// EXCLUDED — submission state is only meaningful for the level-mark
// pipeline that flows into the Report Card Builder's evidence pool.
//
const ProgressOverview = ({
  assessments,
  students,
  results,
  loading,
  onToggleSubmitted,
}) => {
  const levelAssessments = useMemo(
    () => (assessments || []).filter((a) => a.mark_type === "level"),
    [assessments],
  );
  const counts = useMemo(() => {
    let marked = 0;
    let submitted = 0;
    let notIn = 0;
    levelAssessments.forEach((a) => {
      (students || []).forEach((s) => {
        const r = results[`${a.id}|${s.number}`];
        const hasMark =
          !!(r && (r.mark || (r.criterion_levels && Object.keys(r.criterion_levels).length > 0)));
        if (hasMark) marked += 1;
        else if (r?.submitted) submitted += 1;
        else notIn += 1;
      });
    });
    return { marked, submitted, notIn };
  }, [levelAssessments, students, results]);
  const totalCells = (students?.length || 0) * levelAssessments.length;

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-8 text-[#4a6984]">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading progress overview…
      </div>
    );
  }
  if (levelAssessments.length === 0) {
    return (
      <div
        className="rounded-lg border border-dashed border-[#4a6984]/30 bg-[#f4f5f7]/40 p-6 text-center"
        data-testid="progress-overview-empty"
      >
        <ClipboardCheck className="h-6 w-6 text-[#4a6984]/50 mx-auto" />
        <p className="text-sm text-[#4a6984] mt-2">
          No level-mark assessments in this unit yet.
        </p>
        <p className="text-[11px] text-[#4a6984]/65 mt-1">
          The progress overview surfaces submission state for summative
          rubric assessments. Add one in the Marks tab to get started.
        </p>
      </div>
    );
  }
  if ((students || []).length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-[#4a6984]/30 bg-[#f4f5f7]/40 p-6 text-center text-sm text-[#4a6984]">
        Your student roster is empty. Re-run setup to add students.
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="gradebook-progress-overview">
      {/* Summary chips */}
      <div className="flex flex-wrap items-center gap-2 text-[11px]">
        <span
          className="inline-flex items-center gap-1.5 rounded-full bg-[#1c2d3d] text-white px-2.5 py-1 font-extrabold"
          data-testid="progress-summary-marked"
          title="Cells with a teacher-set holistic mark or any rubric criterion level"
        >
          <span className="h-2 w-2 rounded-full bg-white" />
          Marked · {counts.marked}
        </span>
        <span
          className="inline-flex items-center gap-1.5 rounded-full bg-[#96ded1]/40 text-[#1c2d3d] border border-[#96ded1] px-2.5 py-1 font-extrabold"
          data-testid="progress-summary-submitted"
          title="Handed in by the student but not yet marked by the teacher"
        >
          <SubmittedGlyph />
          Handed in, not marked · {counts.submitted}
        </span>
        <span
          className="inline-flex items-center gap-1.5 rounded-full bg-white text-[#4a6984] border border-[#4a6984]/25 px-2.5 py-1 font-extrabold"
          data-testid="progress-summary-not-in"
          title="Not yet handed in"
        >
          <span className="h-2.5 w-2.5 rounded-full border border-[#4a6984]/45 bg-transparent" />
          Not handed in · {counts.notIn}
        </span>
        <span className="text-[10px] italic text-[#4a6984]/65 ml-1">
          {totalCells} cells · click an outlined or mint state pill to toggle
          submission. Marked cells stay marked — clear the mark first to revert.
        </span>
      </div>

      {/* State matrix */}
      <div
        className="overflow-x-auto rounded-lg border border-[#4a6984]/15 bg-white"
        data-testid="progress-overview-matrix"
      >
        <table className="min-w-full text-sm border-collapse">
          <thead>
            <tr className="bg-[#f4f5f7]">
              <th className="sticky left-0 bg-[#f4f5f7] text-left px-3 py-2 text-[10px] font-extrabold uppercase tracking-[0.14em] text-[#4a6984] border-b border-[#4a6984]/15 min-w-[120px] z-10">
                Student
              </th>
              {levelAssessments.map((a) => (
                <th
                  key={a.id}
                  data-testid={`progress-col-${a.id}`}
                  className="text-left px-3 py-2 text-[11px] font-extrabold text-[#1c2d3d] border-b border-l border-[#4a6984]/15 min-w-[140px]"
                >
                  <p className="truncate" title={a.assessment_name}>
                    {a.assessment_name}
                  </p>
                  <p className="text-[9px] font-bold uppercase tracking-wider text-[#4a6984]/70 mt-0.5">
                    {a.assessment_type} · {a.date || "—"}
                  </p>
                  <LinkedTestBadge assessment={a} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {students.map((s) => (
              <tr key={s.number} className="hover:bg-[#f4f5f7]/40">
                <td className="sticky left-0 bg-white hover:bg-[#f4f5f7]/40 px-3 py-2 text-[12px] font-bold text-[#1c2d3d] border-b border-[#4a6984]/10 z-10">
                  {s.label || `Student ${s.number}`}
                </td>
                {levelAssessments.map((a) => {
                  const r = results[`${a.id}|${s.number}`];
                  const hasMark = !!(
                    r &&
                    (r.mark ||
                      (r.criterion_levels &&
                        Object.keys(r.criterion_levels).length > 0))
                  );
                  const submitted = !!r?.submitted;
                  const state = hasMark
                    ? "marked"
                    : submitted
                      ? "submitted"
                      : "not_in";
                  return (
                    <td
                      key={a.id}
                      className="px-3 py-2 border-b border-l border-[#4a6984]/10"
                      data-testid={`progress-cell-${a.id}-${s.number}`}
                    >
                      <ProgressPill
                        state={state}
                        markLabel={r?.mark}
                        criterionFilled={Object.keys(r?.criterion_levels || {}).length}
                        criterionTotal={(a.success_criteria || []).length}
                        onToggle={
                          hasMark
                            ? null
                            : () =>
                                onToggleSubmitted(a.id, s.number, !submitted)
                        }
                        testid={`progress-pill-${a.id}-${s.number}`}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const SubmittedGlyph = () => (
  // Half-filled circle — visual shorthand for "in progress / received".
  <span
    aria-hidden
    className="relative inline-block h-2.5 w-2.5 rounded-full border border-[#1c2d3d] overflow-hidden"
  >
    <span className="absolute inset-y-0 left-0 w-1/2 bg-[#1c2d3d]" />
  </span>
);

// Phase 118.B.3 — small badge in the assessment column header that
// signals the column is wired to a Test Builder test, summarises
// marking progress, and links to the per-student marking page.
const LinkedTestBadge = ({ assessment, compact = false }) => {
  if (!assessment?.linked_test_id) return null;
  const testId = assessment.linked_test_id;
  const summary = assessment.test_summary || { marked_count: 0, average_percent: 0 };
  const marked = Number(summary.marked_count) || 0;
  return (
    <Link
      to={`/tests/${testId}/mark`}
      data-testid={`gradebook-linked-test-${assessment.id}`}
      title="Open the Test Marking page"
      className={
        `inline-flex items-center gap-1 rounded-full border border-[#96ded1] bg-[#96ded1]/20 px-2 py-0.5 ` +
        `text-[10px] font-extrabold uppercase tracking-[0.08em] text-[#1c2d3d] hover:bg-[#96ded1]/40 hover:border-[#1c9e7e] ` +
        `transition-colors ${compact ? "" : "mt-1"}`
      }
    >
      <ClipboardCheck className="h-2.5 w-2.5" />
      {marked === 0 ? (
        <span>Test · Mark students</span>
      ) : (
        <span data-testid={`gradebook-linked-test-summary-${assessment.id}`}>
          {marked} marked · avg {summary.average_percent}%
        </span>
      )}
    </Link>
  );
};


const ProgressPill = ({
  state,
  markLabel,
  criterionFilled,
  criterionTotal,
  onToggle,
  testid,
}) => {
  if (state === "marked") {
    const inner = markLabel
      ? markLabel
      : criterionTotal > 0
        ? `${criterionFilled}/${criterionTotal}`
        : "✓";
    return (
      <span
        data-testid={testid}
        data-state="marked"
        className="inline-flex items-center gap-1.5 rounded-md bg-[#1c2d3d] text-white px-2 py-1 text-[11px] font-extrabold"
        title="Marked — clear the mark in the Marks tab to revert state"
      >
        <span className="h-1.5 w-1.5 rounded-full bg-white" />
        {inner}
      </span>
    );
  }
  if (state === "submitted") {
    return (
      <button
        type="button"
        onClick={onToggle}
        data-testid={testid}
        data-state="submitted"
        className="inline-flex items-center gap-1.5 rounded-md bg-[#96ded1]/40 hover:bg-[#96ded1]/55 text-[#1c2d3d] border border-[#96ded1] px-2 py-1 text-[11px] font-extrabold transition-colors"
        title="Handed in, awaiting marking — click to mark as not handed in"
      >
        <SubmittedGlyph />
        Handed in
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={onToggle}
      data-testid={testid}
      data-state="not_in"
      className="inline-flex items-center gap-1.5 rounded-md bg-white hover:bg-[#96ded1]/15 text-[#4a6984] border border-[#4a6984]/30 hover:border-[#96ded1] px-2 py-1 text-[11px] font-extrabold transition-colors"
      title="Not handed in — click to mark as handed in"
    >
      <span className="h-2.5 w-2.5 rounded-full border border-[#4a6984]/50 bg-transparent" />
      Not in
    </button>
  );
};


// ============================================================================
// Assessment grid
// ============================================================================

const AssessmentGrid = ({ unit, students, onUnitChange, initialPrefill, onPrefillConsumed }) => {
  const [assessments, setAssessments] = useState([]);
  const [results, setResults] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [pendingPrefill, setPendingPrefill] = useState(null);
  // Phase 114.B — per-student rubric panel state.
  const [rubricOpen, setRubricOpen] = useState(false);
  const [rubricTarget, setRubricTarget] = useState(null); // {assessment, studentNumber, studentLabel}
  // Phase 114.D.2.A — tab state for the Marks / Progress overview switch.
  const [activeView, setActiveView] = useState("marks");

  // Auto-open the AddAssessmentForm when arriving with a prefill payload
  // from the Lesson Workspace. We open ONCE per prefill (so the teacher
  // can close the form and the prefill doesn't re-trigger). The Gradebook
  // page already routed us to the matching unit before we mount here.
  useEffect(() => {
    if (initialPrefill && unit?.id === initialPrefill.unitId) {
      const allCriteria = !!initialPrefill.allCriteria;
      const criteriaList = Array.isArray(initialPrefill.criteriaList)
        ? initialPrefill.criteriaList
        : [];
      setPendingPrefill({
        successCriteria: allCriteria
          ? criteriaList.join("\n")
          : initialPrefill.criterion || "",
        codes: (initialPrefill.codes || []).join(", "),
        date: initialPrefill.date || new Date().toISOString().slice(0, 10),
        // Bundle path defaults to summative+level (rubric-shaped); the
        // single-criterion path defaults to formative+completion.
        type: allCriteria ? "summative" : "formative",
        markType: allCriteria ? "level" : "completion",
        name: "",
        bundleMode: allCriteria,
        bundleCriteria: criteriaList,
      });
      setShowAdd(true);
      if (typeof onPrefillConsumed === "function") onPrefillConsumed();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPrefill, unit?.id]);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [aRes, rRes] = await Promise.all([
        listGradebookAssessments(unit.id),
        listGradebookResults({ unitId: unit.id }),
      ]);
      setAssessments(aRes?.assessments || []);
      // Index by `${assessment_id}|${student_number}`
      const idx = {};
      (rRes?.results || []).forEach((r) => {
        idx[`${r.assessment_id}|${r.student_number}`] = r;
      });
      setResults(idx);
    } catch (e) {
      toast.error("Could not load assessments.");
    } finally {
      setLoading(false);
    }
  }, [unit.id]);

  useEffect(() => {
    reload();
  }, [reload]);

  const handleMarkChange = async (assessmentId, studentNumber, mark) => {
    const key = `${assessmentId}|${studentNumber}`;
    setResults((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || {}), mark, assessment_id: assessmentId, student_number: studentNumber },
    }));
    try {
      const res = await upsertGradebookResult({
        assessment_id: assessmentId,
        student_number: studentNumber,
        mark,
      });
      setResults((prev) => ({ ...prev, [key]: res.result }));
    } catch (e) {
      toast.error("Mark didn't save — try again.");
    }
  };

  const handleDeleteAssessment = async (assessmentId) => {
    if (!window.confirm("Delete this assessment and its marks?")) return;
    try {
      await deleteGradebookAssessment(assessmentId);
      toast.success("Assessment removed.");
      reload();
    } catch (e) {
      toast.error("Could not delete assessment.");
    }
  };

  return (
    <section className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-[10px] font-extrabold uppercase tracking-[0.14em] text-[#4a6984]/70 flex items-center gap-1.5">
            <span className="ae-pill !text-[9px] bg-[#96ded1]/15 text-[#1c9e7e] border border-[#96ded1]">
              {PERIOD_LABEL[unit.reporting_period] || unit.reporting_period}
            </span>
            · {SUBJECT_LABEL[unit.subject_key] || unit.subject_key}
          </p>
          <h2
            className="text-xl font-extrabold text-[#1c2d3d] mt-1"
            data-testid="gradebook-unit-title"
          >
            {unit.unit_name}
          </h2>
          {unit.unit_activity_id ? (
            <Link
              to={`/lesson/${unit.unit_activity_id}`}
              data-testid="gradebook-unit-open-link"
              className="inline-flex items-center gap-1 mt-1 text-[11px] font-extrabold uppercase tracking-[0.12em] text-[#1c9e7e] hover:text-[#177a61]"
            >
              Open unit <ExternalLink className="h-3 w-3" />
            </Link>
          ) : null}
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => setShowAdd((o) => !o)}
            data-testid="gradebook-add-assessment-btn"
            className="bg-[#1c2d3d] hover:bg-[#1c2d3d]/90 text-white font-semibold"
            size="sm"
          >
            <Plus className="h-4 w-4 mr-1" />
            Add assessment
          </Button>
        </div>
      </div>

      {/* Phase 114.D.2.A — Marks / Progress overview tab switch. The
          Progress tab surfaces "handed in but not yet marked" cells
          across every level-mark assessment in the unit. */}
      <div
        className="flex items-center gap-1 border-b border-[#4a6984]/15"
        data-testid="gradebook-unit-tabs"
        role="tablist"
      >
        <button
          type="button"
          onClick={() => setActiveView("marks")}
          role="tab"
          aria-selected={activeView === "marks"}
          data-testid="gradebook-tab-marks"
          className={`relative px-4 py-2 text-[11px] font-extrabold uppercase tracking-[0.14em] transition-colors ${
            activeView === "marks"
              ? "text-[#1c2d3d]"
              : "text-[#4a6984]/60 hover:text-[#4a6984]"
          }`}
        >
          Marks
          {activeView === "marks" ? (
            <span className="absolute left-0 right-0 -bottom-px h-[2px] bg-[#1c2d3d]" />
          ) : null}
        </button>
        <button
          type="button"
          onClick={() => setActiveView("progress")}
          role="tab"
          aria-selected={activeView === "progress"}
          data-testid="gradebook-tab-progress"
          className={`relative px-4 py-2 text-[11px] font-extrabold uppercase tracking-[0.14em] transition-colors ${
            activeView === "progress"
              ? "text-[#1c2d3d]"
              : "text-[#4a6984]/60 hover:text-[#4a6984]"
          }`}
        >
          Progress overview
          {activeView === "progress" ? (
            <span className="absolute left-0 right-0 -bottom-px h-[2px] bg-[#1c2d3d]" />
          ) : null}
        </button>
      </div>

      {activeView === "marks" ? (
        <>
          {showAdd ? (
            <AddAssessmentForm
              unit={unit}
              initial={pendingPrefill}
              onCreated={() => {
                setShowAdd(false);
                setPendingPrefill(null);
                reload();
              }}
              onCancel={() => {
                setShowAdd(false);
                setPendingPrefill(null);
              }}
            />
          ) : null}

      {loading ? (
        <div className="flex items-center gap-2 py-8 text-[#4a6984]">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading grid…
        </div>
      ) : assessments.length === 0 ? (
        <div className="rounded-lg border border-dashed border-[#4a6984]/30 bg-[#f4f5f7]/40 p-6 text-center">
          <ClipboardCheck className="h-6 w-6 text-[#4a6984]/50 mx-auto" />
          <p className="text-sm text-[#4a6984] mt-2">
            No assessments yet for this unit.
          </p>
          <p className="text-[11px] text-[#4a6984]/65 mt-1">
            Add one above to start tracking marks.
          </p>
        </div>
      ) : students.length === 0 ? (
        <div className="rounded-lg border border-dashed border-[#4a6984]/30 bg-[#f4f5f7]/40 p-6 text-center text-sm text-[#4a6984]">
          Your student roster is empty. Re-run setup to add students.
        </div>
      ) : (
        <div
          className="overflow-x-auto rounded-lg border border-[#4a6984]/15 bg-white"
          data-testid="gradebook-grid"
        >
          <table className="min-w-full text-sm border-collapse">
            <thead>
              <tr className="bg-[#f4f5f7]">
                <th className="sticky left-0 bg-[#f4f5f7] text-left px-3 py-2 text-[10px] font-extrabold uppercase tracking-[0.14em] text-[#4a6984] border-b border-[#4a6984]/15 min-w-[120px] z-10">
                  Student
                </th>
                {assessments.map((a) => {
                  const colIsRubric =
                    a.mark_type === "level" &&
                    Array.isArray(a.success_criteria) &&
                    a.success_criteria.length >= 2;
                  return (
                  <th
                    key={a.id}
                    data-testid={`gradebook-col-${a.id}`}
                    className="text-left px-3 py-2 text-[11px] font-extrabold text-[#1c2d3d] border-b border-l border-[#4a6984]/15 min-w-[150px]"
                  >
                    <div className="flex items-start justify-between gap-1">
                      <div className="min-w-0">
                        <p className="truncate" title={a.assessment_name}>
                          {a.assessment_name}
                        </p>
                        <p className="text-[9px] font-bold uppercase tracking-wider text-[#4a6984]/70 mt-0.5">
                          {a.assessment_type} · {a.date || "—"}
                        </p>
                        <LinkedTestBadge assessment={a} />
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {colIsRubric ? (
                          <button
                            type="button"
                            onClick={() => {
                              const url = gradebookRubricPdfClassUrl(a.id);
                              window.open(url, "_blank", "noopener,noreferrer");
                            }}
                            data-testid={`gradebook-col-print-class-${a.id}`}
                            className="text-[#4a6984]/55 hover:text-[#1c9e7e]"
                            aria-label="Print rubrics for the whole class"
                            title="Print rubrics for the whole class (one combined PDF)"
                          >
                            <Printer className="h-3 w-3" />
                          </button>
                        ) : null}
                        <button
                          type="button"
                          onClick={() => handleDeleteAssessment(a.id)}
                          data-testid={`gradebook-col-delete-${a.id}`}
                          className="text-[#4a6984]/50 hover:text-red-600"
                          aria-label="Delete assessment"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                    </div>
                  </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {students.map((s) => (
                <tr key={s.number} className="hover:bg-[#f4f5f7]/40">
                  <td className="sticky left-0 bg-white hover:bg-[#f4f5f7]/40 px-3 py-2 text-[12px] font-bold text-[#1c2d3d] border-b border-[#4a6984]/10 z-10">
                    {s.label || `Student ${s.number}`}
                  </td>
                  {assessments.map((a) => {
                    const key = `${a.id}|${s.number}`;
                    const row = results[key];
                    const isRubric =
                      a.mark_type === "level" &&
                      Array.isArray(a.success_criteria) &&
                      a.success_criteria.length >= 2;
                    return (
                      <td
                        key={a.id}
                        className="px-3 py-2 border-b border-l border-[#4a6984]/10"
                        data-testid={`gradebook-cell-${a.id}-${s.number}`}
                      >
                        {isRubric ? (
                          <RubricCellButton
                            row={row}
                            criteriaCount={a.success_criteria.length}
                            onClick={() => {
                              setRubricTarget({
                                assessment: a,
                                studentNumber: s.number,
                                studentLabel: s.label || `Student ${s.number}`,
                              });
                              setRubricOpen(true);
                            }}
                            testid={`gradebook-mark-${a.id}-${s.number}`}
                          />
                        ) : (
                          <MarkCell
                            value={row?.mark || null}
                            markType={a.mark_type}
                            onChange={(v) => handleMarkChange(a.id, s.number, v)}
                            testid={`gradebook-mark-${a.id}-${s.number}`}
                          />
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
        </>
      ) : (
        <ProgressOverview
          assessments={assessments}
          students={students}
          results={results}
          loading={loading}
          onToggleSubmitted={async (assessmentId, studentNumber, nextSubmitted) => {
            const key = `${assessmentId}|${studentNumber}`;
            setResults((prev) => ({
              ...prev,
              [key]: {
                ...(prev[key] || {}),
                assessment_id: assessmentId,
                student_number: studentNumber,
                submitted: nextSubmitted,
              },
            }));
            try {
              const res = await upsertGradebookResult({
                assessment_id: assessmentId,
                student_number: studentNumber,
                submitted: nextSubmitted,
              });
              setResults((prev) => ({ ...prev, [key]: res.result }));
            } catch (e) {
              toast.error(
                e?.response?.data?.detail || "Couldn't update submission state.",
              );
              reload();
            }
          }}
        />
      )}

      {/* Phase 114.B — per-student rubric panel (unbranded, Montserrat) */}
      {rubricTarget ? (
        <RubricPanel
          open={rubricOpen}
          onOpenChange={(o) => {
            setRubricOpen(o);
            if (!o) {
              // Reload results so cell marks reflect any panel edits.
              reload();
            }
          }}
          assessment={rubricTarget.assessment}
          studentNumber={rubricTarget.studentNumber}
          studentLabel={rubricTarget.studentLabel}
          onSaved={(updated) => {
            const k = `${updated.assessment_id}|${updated.student_number}`;
            setResults((prev) => ({ ...prev, [k]: updated }));
          }}
        />
      ) : null}
    </section>
  );
};

// ============================================================================
// Sidebar
// ============================================================================

const Sidebar = ({ units, selectedUnitId, onSelect, studentCount }) => {
  const { user } = useAuth();
  const grouped = useMemo(() => {
    const map = new Map();
    units.forEach((u) => {
      const k = u.subject_key || "other";
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(u);
    });
    const all = Array.from(map.entries()).map(([k, us]) => ({
      key: k,
      label: SUBJECT_LABEL[k] || k,
      units: us,
    }));
    // Phase 116.B — apply the Class Profile master filter. A subject
    // group is hidden if NONE of the canonical subjects it represents
    // are checked in the teacher's profile. Empty profile shows all.
    // Hide ≠ delete: the underlying units stay in the DB; rechecking
    // the subject brings them back instantly. The "other" bucket
    // (legacy units without a subject_key) is always shown so nothing
    // becomes silently unreachable.
    return all.filter((g) => {
      if (g.key === "other") return true;
      return isLrpSubjectKeyVisible(g.key, user?.subjects_taught || []);
    });
  }, [units, user?.subjects_taught]);
  const [expanded, setExpanded] = useState(() => {
    const init = {};
    grouped.forEach((g, i) => {
      init[g.key] = i === 0;
    });
    return init;
  });

  // Expand a subject group if it owns the selected unit so the
  // teacher always sees what's active.
  useEffect(() => {
    const sel = units.find((u) => u.id === selectedUnitId);
    if (sel && sel.subject_key) {
      setExpanded((prev) => ({ ...prev, [sel.subject_key]: true }));
    }
  }, [selectedUnitId, units]);

  return (
    <aside
      className="w-full md:w-64 shrink-0 md:border-r md:border-[#4a6984]/15 md:pr-4"
      data-testid="gradebook-sidebar"
    >
      <div className="rounded-lg border border-[#4a6984]/15 bg-white p-3 md:bg-transparent md:border-0 md:p-0">
        <div className="flex items-center gap-2 mb-2">
          <Users className="h-4 w-4 text-[#4a6984]" />
          <span className="text-[10px] font-extrabold uppercase tracking-[0.14em] text-[#4a6984]">
            Roster · {studentCount} student{studentCount === 1 ? "" : "s"}
          </span>
        </div>
        <div className="space-y-2">
          {grouped.map((g) => (
            <div
              key={g.key}
              className="rounded-md border border-[#4a6984]/15 overflow-hidden"
            >
              <button
                type="button"
                onClick={() =>
                  setExpanded((prev) => ({ ...prev, [g.key]: !prev[g.key] }))
                }
                data-testid={`gradebook-subject-${g.key}`}
                className="w-full flex items-center justify-between gap-2 px-3 py-2 bg-[#f4f5f7] hover:bg-[#e9ecef] text-left"
              >
                <span className="text-[11px] font-extrabold uppercase tracking-[0.12em] text-[#1c2d3d]">
                  {g.label}
                </span>
                <span className="text-[10px] text-[#4a6984]/70">
                  {g.units.length} {expanded[g.key] ? "▾" : "▸"}
                </span>
              </button>
              {expanded[g.key] ? (
                <ul className="border-t border-[#4a6984]/10">
                  {g.units.map((u) => (
                    <li key={u.id}>
                      <button
                        type="button"
                        onClick={() => onSelect(u)}
                        data-testid={`gradebook-unit-${u.id}`}
                        className={`w-full text-left px-3 py-2 text-[12px] border-b border-[#4a6984]/10 last:border-0 ${
                          selectedUnitId === u.id
                            ? "bg-[#96ded1]/20 font-extrabold text-[#1c2d3d]"
                            : "hover:bg-[#f4f5f7]/60 text-[#4a6984]"
                        }`}
                      >
                        {u.unit_name}
                        <span className="block text-[9px] text-[#4a6984]/60 font-semibold uppercase tracking-wider mt-0.5">
                          {PERIOD_LABEL[u.reporting_period] || u.reporting_period}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          ))}
          {grouped.length === 0 ? (
            <div className="rounded-md border border-dashed border-[#4a6984]/30 bg-[#f4f5f7]/40 p-4 text-center text-xs text-[#4a6984]">
              <p>No units yet.</p>
              <Link
                to="/long-range-plan"
                className="inline-block mt-2 text-[10px] font-extrabold uppercase tracking-[0.12em] text-[#1c9e7e] hover:underline"
              >
                Add units in your Long Range Plan
              </Link>
            </div>
          ) : null}
        </div>
      </div>
    </aside>
  );
};

// ============================================================================
// Top-level Gradebook page
// ============================================================================

export default function Gradebook() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [students, setStudents] = useState([]);
  const [units, setUnits] = useState([]);
  const [selectedUnitId, setSelectedUnitId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showSetup, setShowSetup] = useState(false);

  // Phase 114.A.2 — Lesson Workspace deep-link prefill receiver.
  // The teacher clicks "Add to Gradebook" inside a lesson and we land
  // here with ?prefill_unit_activity_id=...&prefill_criterion=...&
  // prefill_codes=...&prefill_date=.... Resolve the matching gradebook
  // unit (by linked activity id) and pass the prefill payload to the
  // assessment grid so it opens the Add Assessment form pre-populated.
  // The teacher reviews and confirms — nothing lands until they do.
  //
  // Phase 114.B — when the teacher chose "Use all criteria" we land
  // here with ?prefill_all_criteria=1 + a JSON array of criteria. We
  // pre-fill a summative bundle column with every criterion stacked,
  // and the form shows a confirmation summary because the blast radius
  // is larger than the single-criterion path.
  const prefillUnitActivityId = searchParams.get("prefill_unit_activity_id");
  const prefillCriterion = searchParams.get("prefill_criterion") || "";
  const prefillAllCriteria = searchParams.get("prefill_all_criteria") === "1";
  const prefillCriteriaList = useMemo(() => {
    const raw = searchParams.get("prefill_criteria_list") || "";
    if (!raw) return [];
    try {
      const arr = JSON.parse(raw);
      return Array.isArray(arr) ? arr.filter(Boolean) : [];
    } catch {
      return [];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  const prefillCodes = (searchParams.get("prefill_codes") || "")
    .split(/[,\s]+/)
    .map((s) => s.trim().toUpperCase())
    .filter(Boolean);
  const prefillDate =
    searchParams.get("prefill_date") || new Date().toISOString().slice(0, 10);
  const prefillSeen = useRef(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const s = await getGradebookSetupStatus();
      setStatus(s);
      if (!s.has_students) {
        setShowSetup(true);
        setLoading(false);
        return;
      }
      const [studentsRes, unitsRes] = await Promise.all([
        getGradebookStudents(),
        getGradebookUnits(),
      ]);
      setStudents(studentsRes.students || []);
      setUnits(unitsRes.units || []);
      // Resolve prefill -> select the matching unit.
      if (prefillUnitActivityId && !prefillSeen.current) {
        const match = (unitsRes.units || []).find(
          (u) => u.unit_activity_id === prefillUnitActivityId
        );
        if (match) {
          setSelectedUnitId(match.id);
        } else if (unitsRes.units?.length && !selectedUnitId) {
          setSelectedUnitId(unitsRes.units[0].id);
        }
      } else if (unitsRes.units?.length && !selectedUnitId) {
        setSelectedUnitId(unitsRes.units[0].id);
      }
    } catch (e) {
      if (e?.response?.status === 402 || e?.response?.status === 403) {
        // Non-Pro — caller will get redirected by the gate below.
        setStatus({ paywall: true });
      } else {
        toast.error("Could not load Gradebook.");
      }
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!user) return;
    loadAll();
  }, [user, loadAll]);

  if (authLoading) return null;
  if (!user) return <Navigate to="/" replace />;

  // Lane 4 (Feb 2026) — replaced the ad-hoc paid check (which missed
  // `trialing`, `past_due`, `beta`, and `is_admin` and incorrectly
  // granted Gradebook to anyone with math-engine access) with the
  // canonical signal from `lib/tier.js`.
  const isPaid = isPaidUser(user);

  if (!isPaid) {
    return (
      <div className="ae-page bg-[#f4f5f7]">
        <main className="ae-container py-12 text-center">
          <ClipboardCheck className="h-10 w-10 text-[#4a6984]/40 mx-auto" />
          <h1 className="text-2xl font-extrabold text-[#1c2d3d] mt-3">
            Gradebook is a Pro feature
          </h1>
          <p className="text-sm text-[#4a6984] mt-2 max-w-md mx-auto">
            Track assessments, enter marks with the Ontario level scale, and
            feed evidence into your Report Card Builder. Upgrade to unlock.
          </p>
          <Button
            onClick={() => navigate("/pricing")}
            className="mt-5 bg-[#1c2d3d] hover:bg-[#1c2d3d]/90 text-white font-semibold"
            data-testid="gradebook-upgrade"
          >
            <Sparkles className="h-4 w-4 mr-1" />
            Upgrade
          </Button>
        </main>
      </div>
    );
  }

  const selectedUnit = units.find((u) => u.id === selectedUnitId);

  return (
    <div className="ae-page bg-[#f4f5f7]">
      <main className="ae-container py-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div>
            <h1 className="text-3xl font-extrabold text-[#1c2d3d]">Gradebook</h1>
            <p className="text-xs text-[#4a6984] mt-1">
              Anonymous assessment tracking — students stay numbered, marks feed
              your Report Card Builder.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowSetup(true)}
            data-testid="gradebook-reopen-setup"
          >
            <Users className="h-4 w-4 mr-1" /> Roster ({students.length})
          </Button>
        </div>

        {showSetup ? (
          <SetupWizard
            status={status || { unit_count: 0, has_lrp: false }}
            onComplete={() => {
              setShowSetup(false);
              loadAll();
            }}
          />
        ) : loading ? (
          <div className="flex items-center gap-2 py-12 text-[#4a6984] justify-center">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading Gradebook…
          </div>
        ) : (
          <div className="flex flex-col md:flex-row gap-5">
            <Sidebar
              units={units}
              selectedUnitId={selectedUnitId}
              onSelect={(u) => setSelectedUnitId(u.id)}
              studentCount={students.length}
            />
            <div className="flex-1 min-w-0">
              {selectedUnit ? (
                <AssessmentGrid
                  unit={selectedUnit}
                  students={students}
                  onUnitChange={loadAll}
                  initialPrefill={
                    prefillUnitActivityId &&
                    !prefillSeen.current &&
                    selectedUnit.unit_activity_id === prefillUnitActivityId
                      ? {
                          unitId: selectedUnit.id,
                          criterion: prefillCriterion,
                          allCriteria: prefillAllCriteria,
                          criteriaList: prefillCriteriaList,
                          codes: prefillCodes,
                          date: prefillDate,
                        }
                      : null
                  }
                  onPrefillConsumed={() => {
                    prefillSeen.current = true;
                    // Clean up the URL so a hard refresh doesn't re-open
                    // the form with stale prefill state.
                    setSearchParams({}, { replace: true });
                  }}
                />
              ) : (
                <div className="rounded-lg border border-dashed border-[#4a6984]/30 bg-white p-8 text-center text-[#4a6984]">
                  <p className="text-sm">Pick a unit from the sidebar to start.</p>
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
